import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { FiLock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { Button } from '@design-system/components/atoms/Button';
import { FormField } from '@design-system/components/molecules/FormField';
import { authService } from '@features/authentication/services/auth.service';
import { APP_ROUTES } from '@shared/constants/routes.constants';

const resetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, 'A senha deve ter no mínimo 8 caracteres')
    .matches(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
    .matches(/[0-9]/, 'A senha deve conter pelo menos um número')
    .required('Senha é obrigatória'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'As senhas devem ser iguais')
    .required('Confirmação de senha é obrigatória'),
});

export interface ResetPasswordFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccess, onError }) => {
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const navigate = useNavigate();

  // Validate session on mount - must have come from verify-code flow
  useEffect(() => {
    const email = sessionStorage.getItem('otp_session_email');
    const otp = sessionStorage.getItem('otp_verified_code');
    const verifiedEmail = sessionStorage.getItem('otp_verified_email');

    // User must have:
    // 1. Email from forgot-password step
    // 2. OTP code from verify-code step
    // 3. Verified email matching the session email
    const isValid = !!(email && otp && verifiedEmail && email === verifiedEmail);

    setIsValidSession(isValid);

    if (!isValid) {
      // Clear any stale session data
      sessionStorage.removeItem('otp_session_token');
      sessionStorage.removeItem('otp_session_email');
      sessionStorage.removeItem('otp_verified_email');
      sessionStorage.removeItem('otp_verified_code');
      void navigate(APP_ROUTES.FORGOT_PASSWORD, { replace: true });
    }
  }, [navigate]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      setServerError('');
      setIsLoading(true);

      const email = sessionStorage.getItem('otp_session_email');
      const otp = sessionStorage.getItem('otp_verified_code');

      try {
        if (!email || !otp) {
          throw new Error('Sessão expirada. Por favor, inicie o processo novamente.');
        }

        // Call API to update password
        await authService.updatePassword(email, otp, values.password);

        // Clear session data after successful password update
        sessionStorage.removeItem('otp_session_token');
        sessionStorage.removeItem('otp_session_email');
        sessionStorage.removeItem('otp_verified_email');
        sessionStorage.removeItem('otp_verified_code');

        onSuccess?.();
        void navigate(APP_ROUTES.LOGIN, { replace: true });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Erro ao criar senha. Tente novamente.';

        setServerError(errorMessage);
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Don't render form until session is validated
  if (isValidSession === null) {
    return null;
  }

  if (!isValidSession) {
    return null;
  }

  const handleBack = () => {
    void navigate(APP_ROUTES.LOGIN);
  };

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <FormField
        name="password"
        label="Senha"
        type="password"
        placeholder="Digite sua nova senha"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.password}
        touched={formik.touched.password}
        required
        fullWidth
        autoComplete="new-password"
        icon={<FiLock className="w-5 h-5" />}
      />

      <FormField
        name="confirmPassword"
        label="Confirmar senha"
        type="password"
        placeholder="Digite sua senha novamente"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.confirmPassword}
        touched={formik.touched.confirmPassword}
        required
        fullWidth
        autoComplete="new-password"
        icon={<FiLock className="w-5 h-5" />}
      />

      {serverError && (
        <div
          className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || !formik.isValid}
      >
        {isLoading ? 'Criando...' : 'Criar senha definitiva'}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleBack}
        disabled={isLoading}
      >
        Voltar
      </Button>
    </form>
  );
};
